import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, Space, Tag } from "antd";
import {
    CheckCircleOutlined,
    EnvironmentOutlined,
    SafetyCertificateOutlined,
    StopOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { AxiosError } from "axios";
import Highlighter from "react-highlight-words";

import { ICity, IPanelUser, IRole } from "../../interfaces";
import PanelUsersService from "../../services/http/services/PanelUsersService";
import RolesService from "../../services/http/services/RolesService";
import CitiesService from "../../services/http/services/CitiesService";
import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";
import { EmitError } from "../../utils/EmitError";
import TableActionButton from "../../components/TableActionButton";
import { ThemeContext } from "../../contexts/ThemeContext";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";

export interface ITablePanelUserData extends IPanelUser {
    key: number;
}

export default function usePanelUsers() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingUpload, setIsLoadingUpload] = useState<boolean>(false);
    const [isUpdateActiveModalVisible, setIsUpdateActiveModalVisible] = useState<boolean>(false);
    const [tableData, setTableData] = useState<Array<ITablePanelUserData>>([]);
    const [panelUserBeingUpdated, setPanelUserBeingUpdated] = useState<ITablePanelUserData | null>(null);
    const [roles, setRoles] = useState<Array<IRole>>([]);
    const [cities, setCities] = useState<Array<ICity>>([]);

    const [searchText, setSearchText] = useState<string>("");
    const [filterRole, setFilterRole] = useState<string | number>("ALL");
    const [filterActive, setFilterActive] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");
    const [filterCity, setFilterCity] = useState<string | number>("ALL");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const navigate = useNavigate();

    const loadData = useCallback(async (signal?: AbortSignal) => {
        try {
            const controller = signal ? undefined : new AbortController();
            const activeSignal = signal || controller!.signal;

            const [panelUsersList, rolesList, citiesList] = await Promise.all([
                PanelUsersService.listPanelUsers(activeSignal),
                RolesService.listRoles(activeSignal),
                CitiesService.listCities(activeSignal),
            ]);

            const mappedData: ITablePanelUserData[] = (panelUsersList || []).map((user: IPanelUser) => ({
                ...user,
                key: user.id || Math.random(),
            }));

            setTableData(mappedData);
            setRoles(rolesList || []);
            setCities((citiesList || []).sort((a: ICity, b: ICity) => `${a.city} - ${a.uf}`.localeCompare(`${b.city} - ${b.uf}`)));
        } catch (err) {
            if (err instanceof AxiosError && err.name === "CanceledError") {
                return;
            }
            EmitError(err, true, "panelUsers_load_error");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setIsLoading(true);
        loadData(controller.signal);

        return () => {
            controller.abort();
        };
    }, [loadData]);

    const handleRefresh = useCallback(async () => {
        setIsLoading(true);
        await loadData();
    }, [loadData]);

    const handleClickCreateNewPanelUser = useCallback(() => {
        navigate("/configuracoes/usuarios-painel/criar");
    }, [navigate]);

    const handleClickEditPanelUser = useCallback(
        (data: IPanelUser) => {
            navigate(`/configuracoes/usuarios-painel/editar/${data.id}`);
        },
        [navigate]
    );

    const handleUpdateActivePanel = useCallback((data: ITablePanelUserData) => {
        setPanelUserBeingUpdated(data);
        setIsUpdateActiveModalVisible(true);
    }, []);

    const handleConfirmUpdateActivePanel = useCallback(async () => {
        if (!panelUserBeingUpdated?.id) return;
        try {
            setIsLoadingUpload(true);
            const newStatus = !panelUserBeingUpdated.active;
            await PanelUsersService.updatePanelUserActive(panelUserBeingUpdated.id, newStatus);
            EmitSuccessAlert(
                newStatus ? "Usuário ativado com sucesso!" : "Usuário inativado com sucesso!"
            );
            setTableData((prev) =>
                prev.map((user) =>
                    user.id === panelUserBeingUpdated.id ? { ...user, active: newStatus } : user
                )
            );
            setIsUpdateActiveModalVisible(false);
            setPanelUserBeingUpdated(null);
        } catch (err) {
            EmitError(err, true, "panelUsers_update_active_error");
        } finally {
            setIsLoadingUpload(false);
        }
    }, [panelUserBeingUpdated]);

    const handleCancelUpdateActivePanel = useCallback(() => {
        setIsUpdateActiveModalVisible(false);
        setPanelUserBeingUpdated(null);
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setSearchText("");
        setFilterRole("ALL");
        setFilterActive("ALL");
        setFilterCity("ALL");
        setCurrentPage(1);
    }, []);

    // Filter table data
    const filteredData = useMemo(() => {
        let result = tableData;

        if (searchText && searchText.trim()) {
            const query = searchText.toLowerCase().trim();
            result = result.filter(
                (user) =>
                    user.name?.toLowerCase().includes(query) ||
                    user.email?.toLowerCase().includes(query)
            );
        }

        if (filterRole !== "ALL") {
            result = result.filter((user) => user.role?.id === Number(filterRole));
        }

        if (filterActive === "ACTIVE") {
            result = result.filter((user) => user.active === true);
        } else if (filterActive === "INACTIVE") {
            result = result.filter((user) => user.active === false);
        }

        if (filterCity !== "ALL") {
            result = result.filter((user) =>
                user.cities?.some((city) => city.id === Number(filterCity))
            );
        }

        return result;
    }, [tableData, searchText, filterRole, filterActive, filterCity]);

    // Active Filter Items
    const activeFilterItems = useMemo((): IActiveFilterItem[] => {
        const items: IActiveFilterItem[] = [];

        if (searchText.trim()) {
            items.push({
                key: "search",
                label: "Busca",
                value: `"${searchText}"`,
                onClear: () => {
                    setSearchText("");
                    setCurrentPage(1);
                },
            });
        }

        if (filterRole !== "ALL") {
            const roleObj = roles.find((r) => r.id === Number(filterRole));
            items.push({
                key: "role",
                label: "Perfil",
                value: roleObj?.name || `Perfil #${filterRole}`,
                onClear: () => {
                    setFilterRole("ALL");
                    setCurrentPage(1);
                },
            });
        }

        if (filterActive !== "ALL") {
            items.push({
                key: "active",
                label: "Status",
                value: filterActive === "ACTIVE" ? "Ativo" : "Inativo",
                onClear: () => {
                    setFilterActive("ALL");
                    setCurrentPage(1);
                },
            });
        }

        if (filterCity !== "ALL") {
            const cityObj = cities.find((c) => c.id === Number(filterCity));
            items.push({
                key: "city",
                label: "Município",
                value: cityObj ? `${cityObj.city} - ${cityObj.uf}` : `Município #${filterCity}`,
                onClear: () => {
                    setFilterCity("ALL");
                    setCurrentPage(1);
                },
            });
        }

        return items;
    }, [searchText, filterRole, filterActive, filterCity, roles, cities]);

    const columns = useMemo(
        () => [
            {
                title: "Usuário",
                dataIndex: "name",
                key: "name",
                sorter: (a: ITablePanelUserData, b: ITablePanelUserData) =>
                    (a.name || "").localeCompare(b.name || ""),
                render: (_: any, record: ITablePanelUserData) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                background: record.active
                                    ? "rgba(22, 119, 255, 0.12)"
                                    : "rgba(148, 163, 184, 0.15)",
                                color: record.active ? "#1677ff" : "#94a3b8",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 15,
                                flexShrink: 0,
                            }}
                        >
                            <UserOutlined />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontWeight: 600, fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b" }}>
                                <Highlighter
                                    highlightStyle={{ backgroundColor: "#ffc069", padding: "0 2px" }}
                                    searchWords={searchText ? [searchText] : []}
                                    autoEscape
                                    textToHighlight={record.name || "--"}
                                />
                            </span>
                            <span style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>
                                <Highlighter
                                    highlightStyle={{ backgroundColor: "#ffc069", padding: "0 2px" }}
                                    searchWords={searchText ? [searchText] : []}
                                    autoEscape
                                    textToHighlight={record.email || "--"}
                                />
                            </span>
                        </div>
                    </div>
                ),
            },
            {
                title: "Perfil",
                dataIndex: "role",
                key: "role",
                width: 170,
                sorter: (a: ITablePanelUserData, b: ITablePanelUserData) =>
                    (a.role?.name || "").localeCompare(b.role?.name || ""),
                render: (_: any, record: ITablePanelUserData) => (
                    <Tag
                        color="purple"
                        style={{
                            borderRadius: 10,
                            margin: 0,
                            fontWeight: 600,
                            fontSize: 11.5,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                        }}
                    >
                        <SafetyCertificateOutlined />
                        {record.role?.name || "Sem perfil"}
                    </Tag>
                ),
            },
            {
                title: "Municípios Vinculados",
                dataIndex: "cities",
                key: "cities",
                width: 230,
                render: (_: any, record: ITablePanelUserData) => {
                    const userCities = record.cities || [];
                    if (userCities.length === 0) {
                        return (
                            <Tag color="default" style={{ borderRadius: 10, margin: 0, fontSize: 11.5 }}>
                                Nenhum município
                            </Tag>
                        );
                    }

                    if (userCities.length === 1) {
                        const c = userCities[0];
                        return (
                            <Tag
                                color="blue"
                                style={{
                                    borderRadius: 10,
                                    margin: 0,
                                    fontSize: 11.5,
                                    fontWeight: 500,
                                }}
                            >
                                <EnvironmentOutlined style={{ marginRight: 4 }} />
                                {c.city} - {c.uf}
                            </Tag>
                        );
                    }

                    const popoverContent = (
                        <div
                            style={{
                                maxWidth: 300,
                                maxHeight: 220,
                                overflowY: "auto",
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                            }}
                        >
                            <div
                                style={{
                                    fontWeight: 600,
                                    fontSize: 12,
                                    marginBottom: 4,
                                    color: isDark ? "#f0f0f0" : "#1e293b",
                                }}
                            >
                                {userCities.length} municípios autorizados:
                            </div>
                            {userCities.map((c) => (
                                <div
                                    key={c.id}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        fontSize: 12,
                                        padding: "4px 8px",
                                        background: isDark ? "#1f1f1f" : "#f8fafc",
                                        borderRadius: 4,
                                        border: `1px solid ${isDark ? "#303030" : "#e2e8f0"}`,
                                        color: isDark ? "#d4d4d4" : "#334155",
                                    }}
                                >
                                    <EnvironmentOutlined style={{ color: "#1677ff" }} />
                                    <span>{c.city} - {c.uf}</span>
                                </div>
                            ))}
                        </div>
                    );

                    return (
                        <Popover
                            content={popoverContent}
                            title={null}
                            trigger="hover"
                            placement="topLeft"
                        >
                            <Tag
                                color="geekblue"
                                style={{
                                    borderRadius: 10,
                                    margin: 0,
                                    fontWeight: 600,
                                    fontSize: 11.5,
                                    cursor: "pointer",
                                }}
                            >
                                <EnvironmentOutlined style={{ marginRight: 4 }} />
                                {userCities.length} municípios
                            </Tag>
                        </Popover>
                    );
                },
            },
            {
                title: "Status",
                dataIndex: "active",
                key: "active",
                width: 110,
                align: "center" as const,
                sorter: (a: ITablePanelUserData, b: ITablePanelUserData) =>
                    (a.active ? 1 : 0) - (b.active ? 1 : 0),
                render: (active: boolean) =>
                    active ? (
                        <Tag
                            color="success"
                            style={{
                                borderRadius: 10,
                                margin: 0,
                                fontWeight: 600,
                                fontSize: 11.5,
                            }}
                        >
                            Ativo
                        </Tag>
                    ) : (
                        <Tag
                            color="error"
                            style={{
                                borderRadius: 10,
                                margin: 0,
                                fontWeight: 600,
                                fontSize: 11.5,
                            }}
                        >
                            Inativo
                        </Tag>
                    ),
            },
            {
                title: "Ações",
                key: "actions",
                width: 100,
                align: "center" as const,
                render: (_: any, record: ITablePanelUserData) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Space size={6}>
                            <TableActionButton
                                action="edit"
                                tooltip="Editar usuário"
                                onClick={() => handleClickEditPanelUser(record)}
                            />
                            <TableActionButton
                                action="custom"
                                icon={
                                    record.active ? (
                                        <StopOutlined style={{ fontSize: 14, color: "#ff4d4f" }} />
                                    ) : (
                                        <CheckCircleOutlined style={{ fontSize: 14, color: "#52c41a" }} />
                                    )
                                }
                                tooltip={record.active ? "Inativar usuário" : "Ativar usuário"}
                                bgColor={
                                    record.active
                                        ? isDark
                                            ? "rgba(255, 77, 79, 0.12)"
                                            : "#fff1f0"
                                        : isDark
                                            ? "rgba(82, 196, 26, 0.12)"
                                            : "#f6ffed"
                                }
                                onClick={() => handleUpdateActivePanel(record)}
                            />
                        </Space>
                    </div>
                ),
            },
        ],
        [isDark, searchText, handleClickEditPanelUser, handleUpdateActivePanel]
    );

    return {
        isLoading,
        isLoadingUpload,
        isUpdateActiveModalVisible,
        panelUserBeingUpdated,
        columns,
        tableData,
        filteredData,
        roles,
        cities,
        searchText,
        setSearchText,
        filterRole,
        setFilterRole,
        filterActive,
        setFilterActive,
        filterCity,
        setFilterCity,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        activeFilterItems,
        handleRefresh,
        handleClickCreateNewPanelUser,
        handleClickEditPanelUser,
        handleUpdateActivePanel,
        handleConfirmUpdateActivePanel,
        handleCancelUpdateActivePanel,
        handleClearAllFilters,
    };
}
